import { Observable } from "core/Misc/observable";
import type { Nullable } from "core/types";
import type { Scene } from "core/scene";
import { GetClass } from "core/Misc/typeStore";
import { serialize } from "core/Misc/decorators";
import { SerializationHelper } from "core/Misc/decorators.serialization";
import { Constants } from "core/Engines/constants";
import { WebRequest } from "core/Misc/webRequest";
import { RewardInputBlock } from "./blocks/RewardInputBlock";
import { PrecisionDate } from "core/Misc/precisionDate";
import type { TeleportOutBlock } from "./blocks/teleport/teleportOutBlock";
import type { TeleportInBlock } from "./blocks/teleport/teleportInBlock";
import { Tools } from "core/Misc/tools";
import type { Color4 } from "core/Maths/math.color";
import { Engine } from "core/Engines/engine";
import { RewardOutputBlock } from "./blocks/RewardOutputBlock";
import { RewardGraphConnectionPointTypes } from "./rewardGraphConnectionPointTypes";
import { RewardGraphNode } from "./rewardGraphNode";
import { RewardGraphBuildState } from "./rewardGraphBuildState";
import { RewardStopOutputBlock } from "./blocks/RewardStopOutputBlock";
import { RewardStateInputBlock } from "./blocks/RewardStateInputBlock";
import { RewardStateOutputBlock } from "./blocks/RewardStateOutputBlock";
import { RewardWaypointOutputBlock } from "./blocks/RewardWaypointOutputBlock";
import { Vector3 } from "core/Maths/math";

declare let NODEREWARDEDITOR: any;
declare let BABYLON: any;

/**
 * Interface used to configure the node reward editor
 */
export interface INodeRewardEditorOptions {
    /** Define the URL to load node editor script from */
    editorURL?: string;
    /** Additional configuration for the NGE */
    nodeRewardEditorConfig?: {
        backgroundColor?: Color4;
        hostScene?: Scene;
        hostMesh?: Scene;
    };
}

/**
 * Defines a node based reward
 * @see demo at https://playground.babylonjs.com#PYY6XE#69
 */
export class NodeReward {
    private static _BuildIdGenerator: number = 0;
    private _buildId: number = NodeReward._BuildIdGenerator++;
    private _buildExecutionTime: number = 0;

    /** Define the Url to load node editor script */
    public static EditorURL = `${Tools._DefaultCdnUrl}/v${Engine.Version}/nodeRewardEditor/babylon.nodeRewardEditor.js`;

    /** Define the Url to load snippets */
    public static SnippetUrl = Constants.SnippetUrl;

    // eslint-disable-next-line @typescript-eslint/naming-convention
    private BJSNODEREWARDEDITOR = this._getGlobalNodeRewardEditor();

    /** @returns the inspector from bundle or global */
    private _getGlobalNodeRewardEditor(): any {
        // UMD Global name detection from Webpack Bundle UMD Name.
        if (typeof NODEREWARDEDITOR !== "undefined") {
            return NODEREWARDEDITOR;
        }

        // In case of module let's check the global emitted from the editor entry point.
        if (typeof BABYLON !== "undefined" && typeof BABYLON.NodeRewardEditor !== "undefined") {
            return BABYLON;
        }

        return undefined;
    }

    /**
     * Gets the time spent to build this block (in ms)
     */
    public get buildExecutionTime() {
        return this._buildExecutionTime;
    }

    /**
     * Gets or sets data used by visual editor
     * @see https://nge.babylonjs.com
     */
    public editorData: any = null;

    /**
     * Gets an array of blocks that needs to be serialized even if they are not yet connected
     */
    public attachedBlocks: RewardGraphNode[] = [];

    /**
     * Observable raised when the reward is built
     */
    public onBuildObservable = new Observable<NodeReward>();

    /** Gets or sets the RewardOutputBlock used to gather the final reward data */
    public outputBlock: Nullable<RewardOutputBlock> = null;
    public stopBlock: Nullable<RewardStopOutputBlock> = null;
    public waypointBlock: Nullable<RewardWaypointOutputBlock> = null;

    /**
     * Snippet ID if the material was created from the snippet server
     */
    public snippetId: string;

    /**
     * The name of the reward
     */
    @serialize()
    public name: string;

    /**
     * A free comment about the reward
     */
    @serialize("comment")
    public comment: string;

    /**
     * Creates a new reward
     * @param name defines the name of the reward
     */
    public constructor(name: string) {
        this.name = name;
    }

    /**
     * Gets the current class name of the reward e.g. "NodeReward"
     * @returns the class name
     */
    public getClassName(): string {
        return "NodeReward";
    }

    /**
     * Get a block by its name
     * @param name defines the name of the block to retrieve
     * @returns the required block or null if not found
     */
    public getBlockByName(name: string) {
        let result = null;
        for (const block of this.attachedBlocks) {
            if (block.name === name) {
                if (!result) {
                    result = block;
                } else {
                    Tools.Warn("More than one block was found with the name `" + name + "`");
                    return result;
                }
            }
        }

        return result;
    }

    /**
     * Get a block using a predicate
     * @param predicate defines the predicate used to find the good candidate
     * @returns the required block or null if not found
     */
    public getBlockByPredicate(predicate: (block: RewardGraphNode) => boolean) {
        for (const block of this.attachedBlocks) {
            if (predicate(block)) {
                return block;
            }
        }

        return null;
    }

    /**
     * Gets the list of input blocks attached to this material
     * @returns an array of InputBlocks
     */
    public getInputBlocks() {
        const blocks: RewardInputBlock[] = [];
        for (const block of this.attachedBlocks) {
            if (block.isInput) {
                blocks.push(block as RewardInputBlock);
            }
        }

        return blocks;
    }

    /**
     * Launch the node reward editor
     * @param config Define the configuration of the editor
     * @returns a promise fulfilled when the node editor is visible
     */
    public edit(config?: INodeRewardEditorOptions): Promise<void> {
        return new Promise((resolve) => {
            this.BJSNODEREWARDEDITOR = this.BJSNODEREWARDEDITOR || this._getGlobalNodeRewardEditor();
            if (typeof this.BJSNODEREWARDEDITOR == "undefined") {
                const editorUrl = config && config.editorURL ? config.editorURL : NodeReward.EditorURL;

                // Load editor and add it to the DOM
                Tools.LoadBabylonScript(editorUrl, () => {
                    this.BJSNODEREWARDEDITOR = this.BJSNODEREWARDEDITOR || this._getGlobalNodeRewardEditor();
                    this._createNodeEditor(config?.nodeRewardEditorConfig);
                    resolve();
                });
            } else {
                // Otherwise creates the editor
                this._createNodeEditor(config?.nodeRewardEditorConfig);
                resolve();
            }
        });
    }

    public closeEditor() {
        if( this.BJSNODEREWARDEDITOR )
            this.BJSNODEREWARDEDITOR.NodeRewardEditor.Hide();
    }

    /**
     * Creates the node editor window.
     * @param additionalConfig Additional configuration for the NGE
     */
    private _createNodeEditor(additionalConfig?: any) {
        const nodeEditorConfig: any = {
            nodeReward: this,
            ...additionalConfig,
        };
        this.BJSNODEREWARDEDITOR.NodeRewardEditor.Show(nodeEditorConfig);
    }

    /**
     * Build the final reward
     * @param verbose defines if the build should log activity
     * @param updateBuildId defines if the internal build Id should be updated (default is true)
     * @param autoConfigure defines if the autoConfigure method should be called when initializing blocks (default is false)
     */
    public build(verbose: boolean = false, updateBuildId = true, autoConfigure = false) {
        if (!this.outputBlock || !this.stopBlock ) {
            // eslint-disable-next-line no-throw-literal
            throw "You must define the outputBlock property before building the rewards";
        }
        const now = PrecisionDate.Now;
        // Initialize blocks
        this._initializeBlock(this.outputBlock, autoConfigure);

        // Build
        const state = new RewardGraphBuildState();

        state.buildId = this._buildId;
        state.verbose = verbose;

        this.outputBlock.build(state);
        this.stopBlock.build(state);
        this.waypointBlock?.build(state);

        if (updateBuildId) {
            this._buildId = NodeReward._BuildIdGenerator++;
        }

        this._buildExecutionTime = PrecisionDate.Now - now;

        // Errors
        state.emitErrors();

        this.onBuildObservable.notifyObservers(this);
    }

    private _initializeBlock(node: RewardGraphNode, autoConfigure = true) {
        node.initialize();
        if (autoConfigure) {
            node.autoConfigure();
        }
        node._preparationId = this._buildId;

        if (this.attachedBlocks.indexOf(node) === -1) {
            this.attachedBlocks.push(node);
        }

        for (const input of node.inputs) {
            const connectedPoint = input.connectedPoint;
            if (connectedPoint) {
                const block = connectedPoint.ownerBlock;
                if (block !== node) {
                    this._initializeBlock(block, autoConfigure);
                }
            }
        }
    }

    /**
     * Clear the current reward
     */
    public clear() {
        this.outputBlock = null;
        this.attachedBlocks.length = 0;
    }

    /**
     * Remove a block from the current reward
     * @param block defines the block to remove
     */
    public removeBlock(block: RewardGraphNode) {
        const attachedBlockIndex = this.attachedBlocks.indexOf(block);
        if (attachedBlockIndex > -1) {
            this.attachedBlocks.splice(attachedBlockIndex, 1);
        }

        if (block === this.outputBlock) {
            this.outputBlock = null;
        }
    }

    /**
     * Clear the current graph and load a new one from a serialization object
     * @param source defines the JSON representation of the reward
     * @param merge defines whether or not the source must be merged or replace the current content
     */
    public parseSerializedObject(source: any, merge = false) {
        if (!merge) {
            this.clear();
        }

        const map: { [key: number]: RewardGraphNode } = {};

        // Create blocks
        for (const parsedBlock of source.blocks) {
            const blockType = GetClass(parsedBlock.customType);
            if (blockType) {
                const block: RewardGraphNode = new blockType();
                block._deserialize(parsedBlock);
                map[parsedBlock.id] = block;

                this.attachedBlocks.push(block);
            }
        }

        // Reconnect teleportation
        for (const block of this.attachedBlocks) {
            if (block.isTeleportOut) {
                const teleportOut = block as TeleportOutBlock;
                const id = teleportOut._tempEntryPointUniqueId;
                if (id) {
                    const source = map[id] as TeleportInBlock;
                    if (source) {
                        source.attachToEndpoint(teleportOut);
                    }
                }
            }
            if( block.isInput ) {
                const input = block as RewardInputBlock;
                const id = input._tempEnablingBlockUniqueId;
                if(id >= 0) {
                    const source = map[id] as RewardInputBlock;
                    if (source) {
                        input._enablingBlock = source;
                    }
                }
            }
        }

        // Reconnect State Capture
        for (const block of this.attachedBlocks) {
            if (block.isStateSink) {
                const stateSink = block as RewardStateInputBlock;
                const id = stateSink._tempStateSourceUniqueId;
                if (id) {
                    const source = map[id] as RewardStateOutputBlock;
                    if (source) {
                        source.attachToSink(stateSink);
                    }
                }
            }
        } 

        // Connections - Starts with input blocks only (except if in "merge" mode where we scan all blocks)
        for (let blockIndex = 0; blockIndex < source.blocks.length; blockIndex++) {
            const parsedBlock = source.blocks[blockIndex];
            const block = map[parsedBlock.id];

            if (!block) {
                continue;
            }

            if (block.inputs.length && parsedBlock.inputs.some((i: any) => i.targetConnectionName) && !merge) {
                continue;
            }
            this._restoreConnections(block, source, map);
        }

        // Outputs
        if (source.outputNodeId !== undefined) {
            this.outputBlock = map[source.outputNodeId] as RewardOutputBlock;
        }
        if (source.outputStopNodeId !== undefined) {
            this.stopBlock = map[source.outputStopNodeId] as RewardStopOutputBlock;
        }
        if (source.outputWaypointNodeId !== undefined) {
            this.waypointBlock = map[source.outputWaypointNodeId] as RewardWaypointOutputBlock;
        }

        // UI related info
        if (source.locations || (source.editorData && source.editorData.locations)) {
            const locations: {
                blockId: number;
                x: number;
                y: number;
            }[] = source.locations || source.editorData.locations;

            for (const location of locations) {
                if (map[location.blockId]) {
                    location.blockId = map[location.blockId].uniqueId;
                }
            }

            if (merge && this.editorData && this.editorData.locations) {
                locations.concat(this.editorData.locations);
            }

            if (source.locations) {
                this.editorData = {
                    locations: locations,
                };
            } else {
                this.editorData = source.editorData;
                this.editorData.locations = locations;
            }

            const blockMap: number[] = [];

            for (const key in map) {
                blockMap[key] = map[key].uniqueId;
            }

            this.editorData.map = blockMap;
        }

        this.comment = source.comment;
    }

    private _restoreConnections(block: RewardGraphNode, source: any, map: { [key: number]: RewardGraphNode }) {
        for (const outputPoint of block.outputs) {
            for (const candidate of source.blocks) {
                const target = map[candidate.id];

                if (!target) {
                    continue;
                }

                for (const input of candidate.inputs) {
                    if (map[input.targetBlockId] === block && input.targetConnectionName === outputPoint.name) {
                        const inputPoint = target.getInputByName(input.inputName);
                        if (!inputPoint || inputPoint.isConnected) {
                            continue;
                        }

                        outputPoint.connectTo(inputPoint, true);
                        this._restoreConnections(target, source, map);
                        continue;
                    }
                }
            }
        }
    }

    /**
     * Generate a string containing the code declaration required to create an equivalent of this reward
     * @returns a string
     */
    public generateCode() {
        let alreadyDumped: RewardGraphNode[] = [];
        const blocks: RewardGraphNode[] = [];
        const uniqueNames: string[] = ["const", "var", "let"];
        // Gets active blocks
        if (this.outputBlock) {
            this._gatherBlocks(this.outputBlock, blocks);
        }

        // Generate
        let codeString = `let nodeReward = new BABYLON.NodeReward("${this.name || "node rewards"}");\n`;
        for (const node of blocks) {
            if (node.isInput && alreadyDumped.indexOf(node) === -1) {
                codeString += node._dumpCode(uniqueNames, alreadyDumped);
            }
        }

        if (this.outputBlock) {
            // Connections
            alreadyDumped = [];
            codeString += "// Connections\n";
            codeString += this.outputBlock._dumpCodeForOutputConnections(alreadyDumped);

            // Output nodes
            codeString += "// Output nodes\n";
            codeString += `nodeReward.outputBlock = ${this.outputBlock._codeVariableName};\n`;
            codeString += `nodeReward.build();\n`;
        }

        return codeString;
    }

    private _gatherBlocks(rootNode: RewardGraphNode, list: RewardGraphNode[]) {
        if (list.indexOf(rootNode) !== -1) {
            return;
        }
        list.push(rootNode);

        for (const input of rootNode.inputs) {
            const connectedPoint = input.connectedPoint;
            if (connectedPoint) {
                const block = connectedPoint.ownerBlock;
                if (block !== rootNode) {
                    this._gatherBlocks(block, list);
                }
            }
        }

        // Teleportation
        if (rootNode.isTeleportOut) {
            const block = rootNode as TeleportOutBlock;
            if (block.entryPoint) {
                this._gatherBlocks(block.entryPoint, list);
            }
        }
    }

    /**
     * Clear the current reward and set it to a default state
     */
    public setToDefault() {
        this.clear();

        this.editorData = null;

        // Source
        const dataBlock = new RewardInputBlock("Reward Default", RewardGraphConnectionPointTypes.Float);
        const dataBlock1 = new RewardInputBlock("Stop Default", RewardGraphConnectionPointTypes.Boolean);
        const dataBlock2 = new RewardInputBlock("Waypoint Default", RewardGraphConnectionPointTypes.Vector3);

        dataBlock1.isConstant = true;
        dataBlock2.value = new Vector3( 0, 0, 1 );

        // Final output
        const output = new RewardOutputBlock("Reward Output");
        const stop = new RewardStopOutputBlock("Stop Output");
        const waypoint = new RewardWaypointOutputBlock("Waypoint Output");
        dataBlock.output.connectTo(output.reward);
        dataBlock1.output.connectTo(stop.stop);
        dataBlock2.output.connectTo(waypoint.waypoint);

        this.outputBlock = output;
        this.stopBlock = stop;
        this.waypointBlock = waypoint;
    }

    /**
     * Makes a duplicate of the current reward.
     * @param name defines the name to use for the new reward
     * @returns the new reward
     */
    public clone(name: string): NodeReward {
        const serializationObject = this.serialize();

        const clone = SerializationHelper.Clone(() => new NodeReward(name), this);
        clone.name = name;

        clone.parseSerializedObject(serializationObject);
        clone._buildId = this._buildId;
        clone.build(false);

        return clone;
    }

    /**
     * Serializes this reward in a JSON representation
     * @param selectedBlocks defines the list of blocks to save (if null the whole reward will be saved)
     * @returns the serialized reward object
     */
    public serialize(selectedBlocks?: RewardGraphNode[]): any {
        const serializationObject = selectedBlocks ? {} : SerializationHelper.Serialize(this);
        serializationObject.editorData = JSON.parse(JSON.stringify(this.editorData)); // Copy

        let blocks: RewardGraphNode[] = [];

        if (selectedBlocks) {
            blocks = selectedBlocks;
        } else {
            serializationObject.customType = "MELS.NodeReward";
            if (this.outputBlock) {
                serializationObject.outputNodeId = this.outputBlock.uniqueId;
            }
            if (this.stopBlock) {
                serializationObject.outputStopNodeId = this.stopBlock.uniqueId;
            }
            if (this.waypointBlock) {
                serializationObject.outputWaypointNodeId = this.waypointBlock.uniqueId;
            }
        }

        // Blocks
        serializationObject.blocks = [];

        for (const block of blocks) {
            serializationObject.blocks.push(block.serialize());
        }

        if (!selectedBlocks) {
            for (const block of this.attachedBlocks) {
                if (blocks.indexOf(block) !== -1) {
                    continue;
                }
                serializationObject.blocks.push(block.serialize());
            }
        }

        return serializationObject;
    }

    /**
     * Disposes the ressources
     */
    public dispose(): void {
        for (const block of this.attachedBlocks) {
            block.dispose();
        }

        this.attachedBlocks.length = 0;
        this.onBuildObservable.clear();
    }

    /**
     * Creates a new node reward set to default basic configuration
     * @param name defines the name of the reward
     * @returns a new NodeReward
     */
    public static CreateDefault(name: string) {
        const nodeReward = new NodeReward(name);

        nodeReward.setToDefault();
        nodeReward.build();

        return nodeReward;
    }

    /**
     * Creates a node reward from parsed reward data
     * @param source defines the JSON representation of the reward
     * @returns a new node reward
     */
    public static Parse(source: any): NodeReward {
        const nodeReward = SerializationHelper.Parse(() => new NodeReward(source.name), source, null);

        nodeReward.parseSerializedObject(source);
        nodeReward.build();

        return nodeReward;
    }

    /**
     * Creates a node reward from a snippet saved by the node reward editor
     * @param snippetId defines the snippet to load
     * @param nodeReward defines a node reward to update (instead of creating a new one)
     * @param skipBuild defines whether to build the node reward
     * @returns a promise that will resolve to the new node reward
     */
    public static ParseFromSnippetAsync(snippetId: string, nodeReward?: NodeReward, skipBuild: boolean = false): Promise<NodeReward> {
        if (snippetId === "_BLANK") {
            return Promise.resolve(NodeReward.CreateDefault("blank"));
        }

        return new Promise((resolve, reject) => {
            const request = new WebRequest();
            request.addEventListener("readystatechange", () => {
                if (request.readyState == 4) {
                    if (request.status == 200) {
                        const snippet = JSON.parse(JSON.parse(request.responseText).jsonPayload);
                        const serializationObject = JSON.parse(snippet.nodeReward);

                        if (!nodeReward) {
                            nodeReward = SerializationHelper.Parse(() => new NodeReward(snippetId), serializationObject, null);
                        }

                        nodeReward.parseSerializedObject(serializationObject);
                        nodeReward.snippetId = snippetId;

                        try {
                            if (!skipBuild) {
                                nodeReward.build();
                            }
                            resolve(nodeReward);
                        } catch (err) {
                            reject(err);
                        }
                    } else {
                        reject("Unable to load the snippet " + snippetId);
                    }
                }
            });

            request.open("GET", this.SnippetUrl + "/" + snippetId.replace(/#/g, "/"));
            request.send();
        });
    }
}
